"use client";
import {NeoModal} from "@/components/@modal/neo.modal";
import {NeoDashboardService} from "@/service/dashboard.service";
import {Fragment, useEffect, useState} from "react";
import toast from "react-hot-toast";
import {BsLightningChargeFill} from "react-icons/bs";
import {FaBus, FaBusAlt} from "react-icons/fa";
import {FaSchool, FaSchoolCircleExclamation} from "react-icons/fa6";
import {
  MdLockOutline,
  MdOutlineHourglassTop,
  MdSyncProblem
} from "react-icons/md";
import {RiDoorOpenLine, RiInformationLine} from "react-icons/ri";
import {RxExit} from "react-icons/rx";
import {TbDoorEnter} from "react-icons/tb";

export interface FeatureMeta {
  id: number;
  name: string;
  desc: string;
  eyebrow: string;
  headline: string;
  sub: string;
  icon: JSX.Element;
}

const AMBER = "#FFC107";
const AMBER_TEXT = "#FFC107";

const BUS_ATTENDANCE = "Bus Attendance";
const CAMPUS_ATTENDANCE = "Campus Attendance";
const ATTENDANCE_INCONSISTENCY = "Attendance Inconsistency Summary";

const FEATURES: FeatureMeta[] = [
  {
    id: 3,
    name: "Bus Attendance",
    desc: "Real-time boarding and drop-off tracking, recorded at every student swipe.",
    eyebrow: "BUS ATTENDANCE",
    headline: "Every boarding, tracked in real time",
    sub: "Every pickup and drop, logged automatically as students swipe in.",
    icon: <FaBus className="h-5 w-5" />
  },
  {
    id: 4,
    name: "Campus Attendance",
    desc: "Real-time tracking of student entry and exit at campus.",
    eyebrow: "CAMPUS ATTENDANCE",
    headline: "Campus presence, live and instant",
    sub: "Every student's Entry and Exit time, captured the instant they swipe.",
    icon: <FaSchool className="h-5 w-5" />
  },
  {
    id: 13,
    name: "Attendance Inconsistency Summary",
    desc: "Automatic detection of mismatches between bus and campus attendance records.",
    eyebrow: "ATTENDANCE INCONSISTENCY SUMMARY",
    headline: "Smart mismatch detection, before it escalates",
    sub: "Automatically detects attendance mismatches by combining Bus and Campus Attendance records.",
    icon: <MdSyncProblem className="h-6 w-6" />
  },
  {
    id: 14,
    name: "Fuel Theft & Refilling",
    desc: "Track fuel consumption and detect theft, refueling, and low fuel events across your fleet.",
    eyebrow: "FUEL THEFT & REFILLING",
    headline: "Spot fuel theft before it drains the budget",
    sub: "Track every refill and catch fuel theft the moment it happens.",
    icon: (
      <svg
        width="20"
        height="18"
        viewBox="0 0 32 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M31.7286 12.4135C31.7286 12.2663 31.6913 12.1211 31.621 11.9923L26.2035 2.05409C25.9694 1.62383 25.428 1.46427 24.9925 1.69601C24.5586 1.92899 24.3973 2.46671 24.6314 2.89697L26.0054 5.41781L24.1959 8.55185C24.073 8.76432 24.0441 9.01657 24.116 9.25036C24.188 9.48415 24.3535 9.67815 24.5747 9.78644L29.7792 12.3401L29.9418 12.6383V19.9158C29.9418 20.9916 29.0595 21.8665 27.9754 21.8665H27.0345C25.9504 21.8665 25.069 20.9916 25.069 19.9158V15.1924C25.069 13.6407 23.7959 12.3782 22.2312 12.3782H20.906V3.13937C20.906 1.40726 19.4865 0 17.741 0H6.07306C4.32638 0 2.90728 1.40767 2.90728 3.13937V28.0903H0.893401C0.399963 28.0903 0 28.4877 0 28.9762C0 29.4647 0.399963 29.8622 0.893401 29.8622H22.4885C22.9811 29.8622 23.3811 29.4647 23.3811 28.9762C23.3811 28.4877 22.9811 28.0903 22.4885 28.0903H20.906V14.1501H22.2308C22.8099 14.1501 23.2818 14.6181 23.2818 15.1924V19.9158C23.2818 21.969 24.9652 23.6384 27.0341 23.6384H27.975C30.0447 23.6384 31.7281 21.969 31.7281 19.9158L31.7286 12.4135ZM26.218 8.61584L27.0055 7.25164L28.3076 9.64124L26.218 8.61584ZM13.8775 22.8123C13.3572 23.4021 12.6561 23.7266 11.9062 23.7266C10.3924 23.7266 9.15943 22.3743 9.15943 20.7135C9.16026 19.2571 11.0132 16.8273 11.3842 16.3556C11.6345 16.0385 12.1767 16.0352 12.4278 16.3556C12.7992 16.8273 14.6526 19.2579 14.6526 20.7135C14.653 21.501 14.3784 22.2471 13.8775 22.8123ZM16.4878 10.1593C16.4878 11.7638 15.1717 13.0698 13.5545 13.0698H9.68017C8.06294 13.0698 6.74601 11.7638 6.74601 10.1593V6.88906C6.74601 5.28451 8.06294 3.97856 9.68017 3.97856H13.5545C15.1717 3.97856 16.4878 5.28451 16.4878 6.88906V10.1593Z"
          fill="currentColor"
        />
      </svg>
    )
  }
];

// The first N features get a rich preview slide with a mock mini dashboard; the rest jump straight to the review screen.
const PREVIEWABLE_COUNT = 5;

export interface MissingFeaturesWidgetProps {
  optedFeatures?: Record<string, boolean>;
  mappedDetails?: any;
  requestedFeatureIds?: number[];
  onRequestSubmitted?: () => void;
}

const MissingFeaturesWidget: React.FC<MissingFeaturesWidgetProps> = ({
  optedFeatures = {},
  mappedDetails = null,
  requestedFeatureIds = [],
  onRequestSubmitted
}) => {
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<"next" | "prev">("next");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [allRequestsOpen, setAllRequestsOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requested, setRequested] = useState<Record<string, boolean>>({});

  // Keep "requested" in sync with what the server currently reports as
  // pending, so features that get approved/rejected drop out of this
  // state instead of staying stuck as "requested" forever.
  useEffect(() => {
    const requestedIdSet = new Set(requestedFeatureIds.map(Number));
    setRequested(prev => {
      const next: Record<string, boolean> = {};
      let changed = false;
      FEATURES.forEach(f => {
        const isRequested = requestedIdSet.has(f.id);
        next[f.name] = isRequested;
        if (!!prev[f.name] !== isRequested) changed = true;
      });
      return changed ? next : prev;
    });
  }, [requestedFeatureIds]);

  // Once both Bus and Campus Attendance are opted or already requested, Attendance
  // Inconsistency Summary comes along automatically - stop suggesting it separately.
  const busSettled =
    !!optedFeatures[BUS_ATTENDANCE] || !!requested[BUS_ATTENDANCE];
  const campusSettled =
    !!optedFeatures[CAMPUS_ATTENDANCE] || !!requested[CAMPUS_ATTENDANCE];
  const attendanceInconsistencySettled = busSettled && campusSettled;

  // Features the account has already opted into shouldn't be suggested as "missing".
  // Attendance Inconsistency only drops out once its prerequisite is settled AND it
  // hasn't itself already been requested - otherwise an already-requested Attendance
  // Inconsistency would disappear from the "requested" views once Bus/Campus settle.
  const availableFeatures = FEATURES.filter(
    f =>
      !optedFeatures[f.name] &&
      !(
        f.name === ATTENDANCE_INCONSISTENCY &&
        attendanceInconsistencySettled &&
        !requested[f.name]
      )
  );
  const remainingFeatures = availableFeatures.filter(f => !requested[f.name]);
  const requestedCount = availableFeatures.length - remainingFeatures.length;

  const goToSlide = (idx: number) => {
    const clamped = Math.max(0, Math.min(idx, remainingFeatures.length - 1));
    setSlideDirection(clamped >= previewIndex ? "next" : "prev");
    setPreviewIndex(clamped);
  };

  const openPreview = (idx: number) => {
    setSlideDirection("next");
    setPreviewIndex(Math.max(0, Math.min(idx, remainingFeatures.length - 1)));
    setPreviewOpen(true);
  };

  // Selections made mid-flow are provisional - discard them if the user closes
  // out without actually submitting a request.
  const closePreview = () => {
    setPreviewOpen(false);
    setSelected({});
  };

  const goToReview = () => {
    setPreviewOpen(false);
    setSubmitted(false);
    setReviewOpen(true);
  };

  const openAllRequests = () => {
    setSubmitted(false);
    setAllRequestsOpen(true);
  };

  const closeAllRequests = () => setAllRequestsOpen(false);

  const handleSubmit = async () => {
    const selectedFeatures = remainingFeatures.filter(f => selected[f.name]);
    if (selectedFeatures.length === 0) return;

    try {
      const response = await NeoDashboardService.onSubmitFeatureRequest({
        feature: selectedFeatures.map(f => f.id)
      });

      if (response?.data?.status !== "success") {
        toast.error(
          response?.data?.message ||
            "Sorry!, We have technical issue please try again later"
        );
        return;
      }
    } catch (error) {
      toast.error("Sorry!, We have technical issue please try again later");
      return;
    }

    setRequested(prev => {
      const next = {...prev};
      selectedFeatures.forEach(f => {
        next[f.name] = true;
      });
      return next;
    });
    setSubmitted(true);
    onRequestSubmitted?.();
  };

  const nextSlide = () => {
    if (previewIndex < remainingFeatures.length - 1) {
      setSlideDirection("next");
      setPreviewIndex(previewIndex + 1);
    } else {
      goToReview();
    }
  };

  // Attendance Inconsistency depends on attendance data from Bus/Campus Attendance.
  const busAttendanceMissing = remainingFeatures.some(
    f => f.name === BUS_ATTENDANCE
  );
  const campusAttendanceMissing = remainingFeatures.some(
    f => f.name === CAMPUS_ATTENDANCE
  );
  const bothAttendanceMissing = busAttendanceMissing && campusAttendanceMissing;

  // When only one of Bus/Campus Attendance is missing (the other is already active),
  // adding it is enough on its own to unlock Attendance Inconsistency - no need to wait for both.
  const attendanceInconsistencyPrerequisiteMet = bothAttendanceMissing
    ? !!selected[BUS_ATTENDANCE] && !!selected[CAMPUS_ATTENDANCE]
    : busAttendanceMissing
      ? !!selected[BUS_ATTENDANCE]
      : campusAttendanceMissing
        ? !!selected[CAMPUS_ATTENDANCE]
        : true;
  const attendanceInconsistencyLocked = !attendanceInconsistencyPrerequisiteMet;

  let attendanceInconsistencyLockedMessage: string | undefined;
  if (attendanceInconsistencyLocked) {
    // Already available in dashboard (not in remainingFeatures) counts as added too.
    const busAdded = !busAttendanceMissing || !!selected[BUS_ATTENDANCE];
    const campusAdded =
      !campusAttendanceMissing || !!selected[CAMPUS_ATTENDANCE];
    if (!busAdded && !campusAdded) {
      attendanceInconsistencyLockedMessage =
        "Select Bus Attendance & Campus Attendance to unlock this.";
    } else if (busAdded && !campusAdded) {
      attendanceInconsistencyLockedMessage =
        "Select Campus Attendance to unlock this.";
    } else if (!busAdded && campusAdded) {
      attendanceInconsistencyLockedMessage =
        "Select Bus Attendance to unlock this.";
    }
  }

  const toggleFeature = (name: string) => {
    setSelected(prev => {
      const next = {...prev, [name]: !prev[name]};
      const isAttendanceName =
        name === BUS_ATTENDANCE || name === CAMPUS_ATTENDANCE;
      const attendanceInconsistencyAvailable = remainingFeatures.some(
        f => f.name === ATTENDANCE_INCONSISTENCY
      );

      if (isAttendanceName && attendanceInconsistencyAvailable) {
        if (next[name]) {
          // Just added Bus/Campus Attendance.
          // Only one of Bus/Campus is on the missing list - adding it alone is enough.
          if (!bothAttendanceMissing) {
            next[ATTENDANCE_INCONSISTENCY] = true;
          } else if (next[BUS_ATTENDANCE] && next[CAMPUS_ATTENDANCE]) {
            // Both are missing - only auto-select once both have been added.
            next[ATTENDANCE_INCONSISTENCY] = true;
          }
        } else {
          // Just removed Bus/Campus Attendance - re-lock the add-on if its
          // prerequisite no longer holds, clearing any stale auto-selection.
          const stillUnlocked = bothAttendanceMissing
            ? !!next[BUS_ATTENDANCE] && !!next[CAMPUS_ATTENDANCE]
            : false;
          if (!stillUnlocked) {
            next[ATTENDANCE_INCONSISTENCY] = false;
          }
        }
      }

      return next;
    });
  };

  const closeReview = () => {
    setReviewOpen(false);
    if (!submitted) {
      setSelected({});
    }
  };

  const allChecked =
    remainingFeatures.length > 0 &&
    remainingFeatures.every(
      f =>
        selected[f.name] ||
        (f.name === ATTENDANCE_INCONSISTENCY && attendanceInconsistencyLocked)
    );
  const toggleAll = () => {
    const next: Record<string, boolean> = {...selected};
    remainingFeatures.forEach(f => {
      if (f.name === ATTENDANCE_INCONSISTENCY) return;
      next[f.name] = !allChecked;
    });

    const attendanceInconsistencyAvailable = remainingFeatures.some(
      f => f.name === ATTENDANCE_INCONSISTENCY
    );
    if (attendanceInconsistencyAvailable) {
      const prerequisiteMet = bothAttendanceMissing
        ? !!next[BUS_ATTENDANCE] && !!next[CAMPUS_ATTENDANCE]
        : busAttendanceMissing
          ? !!next[BUS_ATTENDANCE]
          : campusAttendanceMissing
            ? !!next[CAMPUS_ATTENDANCE]
            : true;
      next[ATTENDANCE_INCONSISTENCY] = prerequisiteMet;
    }

    setSelected(next);
  };

  const currentFeature =
    remainingFeatures[previewIndex] ?? remainingFeatures[0];
  const allRequested =
    availableFeatures.length > 0 && requestedCount === availableFeatures.length;
  const displayFeatures = allRequested ? availableFeatures : remainingFeatures;

  if (availableFeatures.length === 0) {
    return null;
  }

  const requestedFeatures = availableFeatures.filter(f => requested[f.name]);
  // Whenever some features are already requested, show every icon (requested
  // ones marked pending) instead of just the not-yet-requested ones, so the
  // "X/Y REQUESTED" count always matches what's visible in the card.
  const showAllIconsInstead = !allRequested && requestedFeatures.length > 0;
  const rowFeatures = showAllIconsInstead
    ? [...remainingFeatures, ...requestedFeatures]
    : displayFeatures;

  return (
    <div className="relative flex flex-col overflow-hidden rounded-2xl border bg-neo-white  px-3.5 pb-2 pt-3.5 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div
          className="inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold tracking-wide"
          style={
            allRequested
              ? {background: AMBER, color: "black"}
              : requestedCount > 0
                ? {background: AMBER, color: "black"}
                : {background: AMBER, color: "black"}
          }
        >
          {allRequested ? (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 10.41 4.29 4.3-1.42 1.41L11 13V6h2z" />
            </svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2a4 4 0 0 0-4 4v3H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a4 4 0 0 0-4-4zm-2 4a2 2 0 0 1 4 0v3h-4z" />
            </svg>
          )}
          {requestedCount > 0
            ? `${requestedCount}/${availableFeatures.length} REQUESTED`
            : `${availableFeatures.length} FEATURE${
                availableFeatures.length === 1 ? "" : "S"
              }`}
        </div>
        {requestedFeatures.length > 0 && (
          <button
            className="inline-flex items-center gap-1 text-xs font-bold text-default-700 hover:underline"
            onClick={openAllRequests}
          >
            View all Request{requestedFeatures.length === 1 ? "" : "s"}
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      <h3 className="text-base font-bold leading-snug text-default-800">
        {allRequested
          ? "Your request is on its way"
          : availableFeatures.length === 1
            ? "Your dashboard is missing one feature"
            : "Your dashboard is missing a few things"}
      </h3>

      <p className="text-[12px] leading-relaxed text-default-500">
        {allRequested
          ? `Request received for ${availableFeatures.length} ${
              availableFeatures.length === 1 ? "feature" : "features"
            }. Our team will contact you shortly.`
          : requestedCount > 0
            ? `You've requested ${requestedCount} ${
                requestedCount === 1 ? "feature" : "features"
              }. ${
                remainingFeatures.length === 1
                  ? "Request the remaining feature."
                  : "Bundle the remaining requests."
              }`
            : remainingFeatures.length === 1
              ? "Request the feature, and we'll help you get started."
              : "Bundle the requests into one, and we'll handle the rest."}
      </p>
      {displayFeatures.length === 1 && !showAllIconsInstead ? (
        <div
          className="mb-2 mt-4 flex items-start gap-3 rounded-xl border-2 p-3"
          style={
            allRequested
              ? {background: "#F8F9FA", borderColor: "#E5E7EB"}
              : {background: `${AMBER}0D`, borderColor: `${AMBER}24`}
          }
        >
          <div
            className="flex h-11 w-11 flex-shrink-0 cursor-default items-center justify-center rounded-full border-2 bg-white"
            style={{
              color: AMBER,
              borderColor: allRequested ? AMBER_TEXT : AMBER
            }}
          >
            {displayFeatures[0].icon}
          </div>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-bold text-default-800">
                {displayFeatures[0].name}
              </span>
              {allRequested && (
                <span
                  className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
                  style={{background: "#FDF3D9", color: "#FF8F00"}}
                >
                  <MdOutlineHourglassTop className="h-3 w-3" />
                  Requested
                </span>
              )}
            </div>
            <p
              className={`mt-0.5 text-xs leading-relaxed text-default-500 ${
                allRequested ||
                displayFeatures[0].name === "Fuel Theft & Refilling"
                  ? ""
                  : "cursor-pointer"
              }`}
              onClick={() =>
                !allRequested &&
                displayFeatures[0].name !== "Fuel Theft & Refilling" &&
                openPreview(remainingFeatures.indexOf(displayFeatures[0]))
              }
            >
              {displayFeatures[0].desc}
            </p>
          </div>
        </div>
      ) : (
        rowFeatures.length > 0 && (
          <div
            className={`mb-2 mt-4 flex  justify-evenly gap-8 rounded-xl border-2 p-3  ${
              allRequested ? "rounded-2xl p-3" : ""
            }`}
            style={
              allRequested
                ? {background: "#F8F9FA", borderColor: "#E5E7EB"}
                : {background: `${AMBER}0D`, borderColor: `${AMBER}24`}
            }
          >
            {rowFeatures.slice(0, PREVIEWABLE_COUNT - 1).map(f => {
              const isPending = allRequested || !!requested[f.name];
              return (
                <div
                  key={f.name}
                  className="relative flex w-12 flex-col items-center gap-2"
                >
                  <div
                    className="flex h-11 w-11 cursor-default items-center justify-center rounded-full border-2 bg-white"
                    style={{
                      color: AMBER,
                      borderColor: isPending ? AMBER_TEXT : AMBER
                    }}
                  >
                    {f.icon}
                  </div>
                  {isPending && (
                    <span className="absolute right-0 top-8 flex h-4 w-4 items-center  justify-center rounded-full  bg-[#FF8F00]">
                      <MdOutlineHourglassTop className="h-3 w-3 text-white" />
                    </span>
                  )}
                  <span
                    className={`text-center text-[11px] font-bold leading-tight text-default-700 ${
                      isPending ? "" : "cursor-pointer"
                    }`}
                    onClick={() =>
                      !isPending && openPreview(remainingFeatures.indexOf(f))
                    }
                  >
                    {f.name}
                  </span>
                </div>
              );
            })}
            {rowFeatures.length > PREVIEWABLE_COUNT - 1 && (
              <div className="flex w-12 flex-col items-center gap-2">
                <div
                  className="flex h-11 w-fit cursor-default items-center justify-center whitespace-nowrap rounded-full border-2 bg-amber-50 px-3 text-[13px] font-extrabold"
                  style={{
                    color: AMBER,
                    borderColor: AMBER
                  }}
                >
                  +{rowFeatures.length - (PREVIEWABLE_COUNT - 1)}
                </div>
                <span
                  className={`text-center text-[11px] font-bold leading-tight text-default-700 ${
                    allRequested ? "" : "cursor-pointer"
                  }`}
                  onClick={() =>
                    !allRequested && openPreview(PREVIEWABLE_COUNT - 1)
                  }
                >
                  More
                </span>
              </div>
            )}
          </div>
        )
      )}
      {allRequested ? (
        <button
          disabled
          className="mb-3 mt-auto inline-flex cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-gray-200 px-10 py-2 text-sm font-bold text-[#5F6368]"
        >
          <svg
            width="10"
            height="16"
            viewBox="0 0 10 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 1.07253V0.536C10 0.24 9.79609 0 9.54562 0H0.454381C0.203454 0 0 0.24 0 0.536V1.072C0 1.33333 0.161407 1.5408 0.370739 1.58827C0.370287 1.67253 0.36893 1.7616 0.36893 1.8432C0.358984 3.15093 0.658739 3.91733 3.50393 6.93653C3.52609 6.9568 3.64319 7.05973 3.76933 7.256C3.89637 7.4512 4.0094 7.71627 4.0094 8.00747C4.0094 8.29707 3.89637 8.56213 3.76933 8.7584C3.64273 8.9536 3.52383 9.0512 3.52609 9.056C0.659644 12.0933 0.358532 12.8597 0.36893 14.1685C0.36893 14.2464 0.370287 14.3312 0.370739 14.4117C0.161859 14.4597 0 14.6672 0 14.9275V15.4635C0 15.76 0.204358 16 0.454381 16H9.54517C9.79609 16 9.99955 15.76 9.99955 15.464V14.928C9.99955 14.6667 9.83678 14.4587 9.62655 14.4107C9.627 14.3312 9.6279 14.2459 9.6279 14.1701C9.63785 12.8597 9.33584 12.0933 6.49335 9.07733C6.4703 9.056 6.35365 8.9536 6.22886 8.7584C6.10137 8.56213 5.98698 8.2992 5.98879 8.00747C5.98698 7.71627 6.10137 7.45173 6.22886 7.256C6.35365 7.05973 6.47301 6.9632 6.47165 6.9568C9.33674 3.9184 9.6365 3.152 9.6279 1.8432C9.627 1.7632 9.627 1.6736 9.62655 1.58987C9.83724 1.54293 10 1.3344 10 1.07253ZM9.01076 1.8432C9.00172 2.90027 8.95244 3.29867 6.07966 6.39733C6.08328 6.40373 5.39063 6.97387 5.373 8.00747C5.39063 9.03947 6.08328 9.6096 6.10182 9.6384C8.95108 12.7147 9.00172 13.1109 9.01076 14.1707C9.01121 14.2267 9.01121 14.2848 9.01121 14.3461C7.90985 14.3461 5.00407 14.3461 4.9991 14.3461C4.9991 14.3461 2.08699 14.3461 0.986527 14.3461C0.985623 14.2832 0.985623 14.2245 0.985623 14.168C0.996021 13.1099 1.04621 12.7147 3.91808 9.61547C3.91401 9.60907 4.60801 9.0384 4.62564 8.0064C4.60801 6.9728 3.91536 6.40267 3.89637 6.37493C1.04395 3.29493 0.996021 2.90027 0.985623 1.8432C0.985623 1.78773 0.985623 1.72907 0.986527 1.66773C2.08744 1.66773 4.9991 1.6688 4.9991 1.6688C4.9991 1.6688 7.90849 1.66773 9.01076 1.66773C9.01076 1.728 9.01076 1.78613 9.01076 1.8432Z"
              fill="#FF8F00"
            />
            <path
              d="M8.22317 12.3386C6.35817 12.512 6.49516 11.4928 5.6809 11.1013C5.54842 11.0373 5.30564 10.7968 5.30564 10.4869C5.30564 10.1765 5.30564 7.73918 5.30564 7.73918C5.30564 7.48531 5.31061 7.26825 5.31875 7.07998C5.45303 6.74558 5.65106 6.43625 5.91283 6.18291C6.56117 5.48318 7.05851 4.92851 7.44507 4.47251C6.49426 4.47411 5.78036 4.05651 5.06737 3.63625C4.27435 3.16798 2.97179 3.60371 2.14351 3.97385C2.56262 4.50398 3.17298 5.19731 4.07451 6.17065C4.44163 6.52851 4.69527 6.95305 4.80649 7.44478C4.81011 7.54078 4.81463 7.63518 4.81463 7.73971C4.81463 7.73971 4.81463 10.2864 4.81463 10.504C4.81463 10.7221 4.75315 10.8448 4.43078 11.1408C4.10933 11.4368 3.77747 11.2074 3.32309 11.4757C2.77286 11.8005 2.81084 12.3365 1.6941 12.4586C1.1927 13.1957 1.13528 13.6069 1.13347 14.1733H5.00046H8.86699C8.86518 13.577 8.80188 13.1525 8.22317 12.3386Z"
              fill="#FF8F00"
            />
          </svg>
          Request in progress
        </button>
      ) : (
        remainingFeatures.length > 0 && (
          <button
            className="mt-auto inline-flex items-center justify-center gap-2 rounded-xl bg-black px-5 py-3.5 text-sm font-extrabold text-white transition-transform"
            onClick={() => openPreview(0)}
          >
            {requestedCount > 0
              ? `Request remaining feature${
                  remainingFeatures.length === 1 ? "" : "s"
                }`
              : "Review & request"}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </button>
        )
      )}

      <NeoModal
        actionType="missingFeaturePreview"
        onOpen={previewOpen && !!currentFeature}
        onClose={closePreview}
        hasActions={false}
        data={
          currentFeature
            ? {
                feature: currentFeature,
                index: previewIndex,
                total: remainingFeatures.length,
                added: !!selected[currentFeature.name],
                direction: slideDirection,
                locked:
                  currentFeature.name === ATTENDANCE_INCONSISTENCY &&
                  attendanceInconsistencyLocked,
                lockedMessage:
                  currentFeature.name === ATTENDANCE_INCONSISTENCY
                    ? attendanceInconsistencyLockedMessage
                    : undefined,
                onClose: closePreview,
                onJump: goToSlide,
                onToggleAdd: () => toggleFeature(currentFeature.name),
                onNext: nextSlide,
                onReview: goToReview,
                attendanceContext:
                  currentFeature.name === ATTENDANCE_INCONSISTENCY
                    ? {
                        busAdded:
                          !!optedFeatures[BUS_ATTENDANCE] ||
                          !!selected[BUS_ATTENDANCE] ||
                          !!requested[BUS_ATTENDANCE],
                        campusAdded:
                          !!optedFeatures[CAMPUS_ATTENDANCE] ||
                          !!selected[CAMPUS_ATTENDANCE] ||
                          !!requested[CAMPUS_ATTENDANCE],
                        busOpted: !!optedFeatures[BUS_ATTENDANCE],
                        campusOpted: !!optedFeatures[CAMPUS_ATTENDANCE],
                        busRequested: !!requested[BUS_ATTENDANCE],
                        campusRequested: !!requested[CAMPUS_ATTENDANCE],
                        locked: attendanceInconsistencyLocked,
                        onJumpToFeature: (name: string) => {
                          const idx = remainingFeatures.findIndex(
                            f => f.name === name
                          );
                          if (idx >= 0) goToSlide(idx);
                        },
                        onToggleFeature: (name: string) => toggleFeature(name)
                      }
                    : undefined,
                mappedDetails
              }
            : null
        }
      />

      <NeoModal
        actionType="missingFeatureReview"
        onOpen={reviewOpen}
        onClose={closeReview}
        hasActions={false}
        data={{
          features: remainingFeatures,
          requestedFeatures,
          selected,
          allChecked,
          attendanceInconsistencyLocked,
          attendanceInconsistencyLockedMessage,
          submitted,
          onToggleFeature: toggleFeature,
          onToggleAll: toggleAll,
          onSubmit: handleSubmit,
          onClose: closeReview
        }}
      />

      <NeoModal
        actionType="missingFeatureAllRequests"
        onOpen={allRequestsOpen}
        onClose={closeAllRequests}
        hasActions={false}
        data={{
          features: remainingFeatures,
          requestedFeatures,
          selected,
          attendanceInconsistencyLocked,
          attendanceInconsistencyLockedMessage,
          submitted,
          onSubmit: handleSubmit,
          onPreviewRequest: () => {
            setAllRequestsOpen(false);
            openPreview(0);
          },
          onClose: closeAllRequests
        }}
      />
    </div>
  );
};

const formatShortDateTime = (d: Date) => {
  const day = d.getDate();
  const month = d.toLocaleDateString("en-US", {month: "short"});
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${day} ${month}, ${hours}:${minutes} ${ampm}`;
};

// Random date within the current week, no later than today; time is fixed per call.
const randomRecentDateTime = (hours: number, minutes: number) => {
  const daysAgo = Math.floor(Math.random() * 7);
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(hours, minutes, 0, 0);
  return formatShortDateTime(d);
};

const PreviewSlideContent: React.FC<{
  name: string;
  attendanceContext?: AttendanceInconsistencyContext;
  mappedDetails?: any;
}> = ({name, attendanceContext, mappedDetails}) => {
  const [flipped, setFlipped] = useState(false);
  const mappedVehicleDetails: string[] = Array.isArray(
    mappedDetails?.vehicleDetails
  )
    ? mappedDetails.vehicleDetails
    : [];
  const mappedUserDetails: string[] = Array.isArray(mappedDetails?.userDetails)
    ? mappedDetails.userDetails
    : [];
  const todayLabel = new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
  if (name === "Bus Attendance") {
    return (
      <div className="mt-2 flex flex-col gap-3 px-4 md:flex-row md:items-start">
        {/* Image 1: Dashboard summary */}
        <div className="w-full max-w-[550px] md:flex-1">
          <div
            className="mb-1.5 text-xs font-extrabold uppercase tracking-wide"
            style={{color: AMBER_TEXT}}
          >
            Dashboard
          </div>
          <div className="w-full rounded-2xl border border-gray-200 bg-white p-4 shadow-md">
            <div className="mb-3 flex items-center justify-between">
              <span className=" font-semibold text-default-800">
                Bus Attendance
              </span>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                className="text-default-400"
              >
                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </div>

            <div
              className="mb-2 flex items-center gap-3 rounded-full px-4 py-2"
              style={{background: "#E8F0FE"}}
            >
              <div
                className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
                style={{background: AMBER}}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 10 12 5 2 10l10 5 10-5Z" />
                  <path d="M6 12v5c0 1.5 3 3 6 3s6-1.5 6-3v-5" />
                </svg>
              </div>
              <div className="flex-1 font-semibold text-default-800">
                Total Users
              </div>
              <div className=" font-bold text-default-900">250</div>
            </div>

            <div className="mb-2 ml-2 flex items-center">
              <div className="flex flex-1 items-center gap-3">
                <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                <div>
                  <div className="text-sm text-default-400">Present</div>
                  <div className=" font-bold text-default-900">210</div>
                </div>
              </div>
              <div className="mx-4 h-10 w-px border-l border-dashed border-gray-300" />
              <div className="flex flex-1 items-center gap-3">
                <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-red-500 text-white">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </span>
                <div>
                  <div className="text-sm text-default-400">Absent</div>
                  <div className=" font-bold text-default-900">40</div>
                </div>
              </div>
            </div>

            <div
              className="mb-2 flex items-center gap-3 rounded-full px-4 py-2"
              style={{background: "#FDF3D9"}}
            >
              <div
                className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
                style={{background: AMBER}}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 10 12 5 2 10l10 5 10-5Z" />
                  <path d="M6 12v5c0 1.5 3 3 6 3s6-1.5 6-3v-5" />
                </svg>
              </div>
              <div className="flex-1 text-base font-semibold text-default-800">
                Total Staff
              </div>
              <div className=" font-extrabold text-default-900">40</div>
            </div>

            <div className=" ml-2 flex items-center">
              <div className="flex flex-1 items-center gap-3">
                <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                <div>
                  <div className="text-sm text-default-400">Present</div>
                  <div className="text-lg font-bold text-default-900">35</div>
                </div>
              </div>
              <div className="mx-4 h-10 w-px border-l border-dashed border-gray-300" />
              <div className="flex flex-1 items-center gap-3">
                <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-red-500 text-white">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </span>
                <div>
                  <div className="text-sm text-default-400">Absent</div>
                  <div className="text-lg font-bold text-default-900">5</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Image 2: Bus Attendance detail view */}
        <div className="w-full max-w-[550px] md:flex-1">
          <div
            className="mb-1.5 text-xs font-extrabold uppercase tracking-wide"
            style={{color: AMBER_TEXT}}
          >
            Detail view
          </div>
          <div className="overflow-hidden rounded-2xl border  border-gray-200 bg-white p-3 shadow-md">
            <div className="mb-1 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  className="text-default-500"
                >
                  <path d="M4 6h16M4 12h10M4 18h6" />
                </svg>
                <span className="text-base font-bold text-default-800">
                  Bus Attendance
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 rounded-full border border-gray-200 px-2.5 py-1 text-[10px] text-default-400">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  Search..
                </div>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  className="text-default-400"
                >
                  <path d="M12 15V3m0 12-4-4m4 4 4-4M4 21h16" />
                </svg>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  className="text-default-400"
                >
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
              </div>
            </div>
            <div className="mb-1.5 text-[10px] font-semibold text-default-400">
              Total Data : 6
            </div>

            <div className="mb-1.5 flex w-fit items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1 text-[10px] font-bold text-default-700">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path d="M4 6h16M7 12h10M10 18h4" />
              </svg>
              Grouped by: Vehicle
              <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </div>

            <div className="rounded-lg border border-gray-100">
              <div>
                <div className="grid grid-cols-[1fr_1.1fr_0.8fr_0.8fr_0.9fr_0.8fr_0.9fr] gap-1 border-b border-gray-100 bg-gray-50 px-2 py-1 text-[8px] font-bold text-default-500">
                  <span>Vehicle</span>
                  <span>Date Range</span>
                  <span className="text-center">Assigned</span>
                  <span className="text-center">Present</span>
                  <span className="text-center">Un Assigned</span>
                  <span className="text-center">Absent</span>
                  <span>Attendance %</span>
                </div>
                {[
                  {
                    name: "TS01T144",
                    date: todayLabel,
                    days: "1 day",
                    assigned: 31,
                    present: 31,
                    unassigned: 1,
                    absent: 0
                  },
                  {
                    name: "TN37BY123",
                    date: todayLabel,
                    days: "1 day",
                    assigned: 9,
                    present: 5,
                    unassigned: 0,
                    absent: 4
                  },
                  {
                    name: "TN37BY8764",
                    date: todayLabel,
                    days: "1 day",
                    assigned: 34,
                    present: 30,
                    unassigned: 0,
                    absent: 4
                  },
                  {
                    name: "TN37BY8764",
                    date: todayLabel,
                    days: "1 day",
                    assigned: 6,
                    present: 1,
                    unassigned: 1,
                    absent: 4
                  }
                ].map((row, i, arr) => (
                  <div
                    key={row.name}
                    className={`grid grid-cols-[1fr_1.1fr_0.8fr_0.8fr_0.9fr_0.8fr_0.9fr] items-center gap-1 px-2 py-1 text-[9px] ${
                      i !== arr.length - 1 ? "border-b border-gray-50" : ""
                    }`}
                  >
                    <span className="font-semibold text-default-800">
                      {mappedVehicleDetails[i] ?? row.name}
                    </span>
                    <div>
                      <div className="font-semibold text-default-800">
                        {row.date}
                      </div>
                      <div className="text-[8px] text-default-400">
                        {row.days}
                      </div>
                    </div>
                    <span className="text-center font-semibold text-default-800">
                      {row.assigned}
                    </span>
                    <span className="text-center font-semibold text-green-600">
                      {row.present}
                    </span>
                    <span className="text-center font-semibold text-amber-500">
                      {row.unassigned}
                    </span>
                    <span className="text-center font-semibold text-red-500">
                      {row.absent}
                    </span>
                    {(() => {
                      const percent = row.assigned
                        ? (row.present / row.assigned) * 100
                        : 0;
                      const clamped = Math.min(100, Math.max(0, percent));
                      return (
                        <div>
                          <div className="mb-0.5 font-semibold text-default-800">
                            {clamped.toFixed(2)}%
                          </div>
                          <div className="h-1 w-12 overflow-hidden rounded-full bg-gray-100">
                            <div
                              className="h-full rounded-full bg-gray-400"
                              style={{width: `${clamped}%`}}
                            />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (name === "Campus Attendance") {
    return (
      <div className="mt-2 flex flex-col gap-3 px-4 md:flex-row md:items-start">
        {/* Image 1: Dashboard summary */}
        <div className="w-full max-w-[550px] md:flex-1">
          <div
            className="mb-1.5 text-xs font-extrabold uppercase tracking-wide"
            style={{color: AMBER_TEXT}}
          >
            Dashboard
          </div>
          <div className="w-full rounded-2xl border border-gray-200 bg-white p-2 shadow-md">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-lg font-bold text-default-800">
                Campus Attendance
              </span>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                className="text-default-400"
              >
                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </div>

            {[
              {
                label: "Total Users",
                groupLabel: "USERS",
                total: 250,
                present: 40,
                absent: 210,
                bg: "#EAF0FE",
                accent: "#3B82F6"
              },
              {
                label: "Total Staff",
                groupLabel: "STAFF",
                total: 100,
                present: 90,
                absent: 10,
                bg: "#FDF3D9",
                accent: "#F59E0B"
              }
            ].map((section, i) => {
              const presentPercent = section.total
                ? (section.present / section.total) * 100
                : 0;
              const absentPercent = section.total
                ? (section.absent / section.total) * 100
                : 0;
              return (
                <div
                  key={section.label}
                  className={`flex items-center gap-3 rounded-2xl p-1`}
                >
                  <div
                    className="flex w-24 flex-shrink-0 flex-col items-center gap-1 rounded-xl px-4 py-2"
                    style={{background: section.bg}}
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={section.accent}
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 20c0-4 3.5-6 8-6s8 2 8 6" />
                      </svg>
                    </div>
                    <div className="text-center text-[11px] text-default-500">
                      {section.label}
                    </div>
                    <div className="text-lg font-bold text-default-900">
                      {section.total}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div
                      className="mb-1 flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wide"
                      style={{color: section.accent}}
                    >
                      <svg
                        width="8"
                        height="8"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2 3 22h18Z" />
                      </svg>
                      {section.groupLabel}
                    </div>
                    <div className="mb-2 flex items-center gap-2">
                      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-green-500" />
                      <span className="w-11 flex-shrink-0 text-[11px] text-default-500">
                        Present
                      </span>
                      <span className="w-4 flex-shrink-0 text-[11px] font-semibold text-default-800">
                        {section.present}
                      </span>
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                        <div
                          className="h-full rounded-full bg-green-400"
                          style={{width: `${presentPercent}%`}}
                        />
                      </div>
                      <span className="w-12 flex-shrink-0 text-right text-[10px] text-default-400">
                        {presentPercent.toFixed(2)}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500" />
                      <span className="w-11 flex-shrink-0 text-[11px] text-default-500">
                        Absent
                      </span>
                      <span className="w-4 flex-shrink-0 text-[11px] font-semibold text-default-800">
                        {section.absent}
                      </span>
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                        <div
                          className="h-full rounded-full bg-red-400"
                          style={{width: `${absentPercent}%`}}
                        />
                      </div>
                      <span className="w-12 flex-shrink-0 text-right text-[10px] text-default-400">
                        {absentPercent.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Image 2: Campus Attendance detail view */}
        <div className="w-full max-w-[550px] md:flex-1">
          <div
            className="mb-1.5 text-[11px] font-extrabold uppercase tracking-wide"
            style={{color: AMBER_TEXT}}
          >
            Detail view
          </div>
          <div className="overflow-hidden rounded-2xl border  border-gray-200 bg-white p-3 shadow-md">
            <div className="mb-1 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  className="text-default-500"
                >
                  <path d="M4 6h16M4 12h10M4 18h6" />
                </svg>
                <span className="text-base font-bold text-default-800">
                  Campus Attendance
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 rounded-full border border-gray-200 px-2.5 py-1 text-[10px] text-default-400">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  Search..
                </div>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  className="text-default-400"
                >
                  <path d="M12 15V3m0 12-4-4m4 4 4-4M4 21h16" />
                </svg>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  className="text-default-400"
                >
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
              </div>
            </div>
            <div className="mb-1 text-[10px] font-semibold text-default-400">
              Total Data : 250
            </div>

            <div className="mb-2 flex w-fit items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1 text-[10px] font-bold text-default-700">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path d="M4 6h16M7 12h10M10 18h4" />
              </svg>
              Grouped by: Class
              <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </div>

            <div className="rounded-lg border border-gray-100">
              <div>
                <div className="grid grid-cols-[0.8fr_1.1fr_0.9fr_0.9fr_0.9fr] gap-1 border-b border-gray-100 bg-gray-50 px-2 py-1 text-[8px] font-bold text-default-500">
                  <span>Class</span>
                  <span>Date Range Summary</span>
                  <span className="text-center">Total</span>
                  <span className="text-center">Present</span>
                  <span>Attendance %</span>
                </div>
                {[
                  {
                    name: "II",
                    date: todayLabel,
                    days: "1 day",
                    total: 60,
                    present: 56
                  },
                  {
                    name: "IV",
                    date: todayLabel,
                    days: "1 day",
                    total: 50,
                    present: 50
                  },
                  {
                    name: "VII",
                    date: todayLabel,
                    days: "1 day",
                    total: 60,
                    present: 54
                  },
                  {
                    name: "x",
                    date: todayLabel,
                    days: "1 day",
                    total: 70,
                    present: 50
                  }
                ].map((row, i, arr) => {
                  const attendancePercent =
                    row.total > 0 ? (row.present / row.total) * 100 : 0;
                  return (
                    <div
                      key={row.name}
                      className={`grid grid-cols-[0.8fr_1.1fr_0.9fr_0.9fr_0.9fr] items-center gap-1 px-2 py-1 text-[9px] ${
                        i !== arr.length - 1 ? "border-b border-gray-50" : ""
                      }`}
                    >
                      <span className="font-semibold text-default-800">
                        {row.name}
                      </span>
                      <div>
                        <div className="font-semibold text-default-800">
                          {row.date}
                        </div>
                        <div className="text-[8px] text-default-400">
                          {row.days}
                        </div>
                      </div>
                      <span className="text-center font-semibold text-default-800">
                        {row.total}
                      </span>
                      <span className="text-center font-semibold text-green-600">
                        {row.present}
                      </span>
                      <div>
                        <div className="mb-0.5 font-semibold text-default-500">
                          {attendancePercent.toFixed(2)}%
                        </div>
                        <div className="h-1 w-12 overflow-hidden rounded-full bg-gray-100">
                          <div
                            className="h-full rounded-full bg-blue-400"
                            style={{width: `${attendancePercent}%`}}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (name === "Attendance Inconsistency Summary") {
    const busAdded = !!attendanceContext?.busAdded;
    const campusAdded = !!attendanceContext?.campusAdded;
    const isLocked = !!attendanceContext?.locked;
    return (
      <div className="flex flex-col gap-3 px-4 md:flex-row md:items-stretch">
        {attendanceContext && (
          <div className="w-full md:max-w-none md:basis-[40%]">
            <div className="mt-8 flex h-[360px] flex-col rounded-2xl border border-gray-200 bg-white p-3.5 shadow-sm">
              <div className="mb-1.5 flex items-center gap-1.5">
                <div className="flex h-6 w-6 items-center justify-center text-black">
                  <RiInformationLine />
                </div>
                <span className=" font-extrabold text-default-800">
                  Included Automatically
                </span>
              </div>
              <p className="mb-5 text-[12px] leading-relaxed text-default-500">
                This feature becomes available automatically after both required
                modules are enabled. No additional request is needed.
              </p>

              {[
                {
                  label: BUS_ATTENDANCE,
                  done: busAdded,
                  opted: attendanceContext.busOpted,
                  requested: attendanceContext.busRequested
                },
                {
                  label: CAMPUS_ATTENDANCE,
                  done: campusAdded,
                  opted: attendanceContext.campusOpted,
                  requested: attendanceContext.campusRequested
                }
              ]
                .filter(row => row.opted || row.requested)
                .map(row => (
                  <div
                    key={row.label}
                    className="mb-3 flex w-full items-center gap-2 rounded-xl border p-2"
                    style={{
                      background: "#FF8F000D",
                      borderColor: `${AMBER}2E`
                    }}
                  >
                    <div
                      className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
                      style={{background: `${AMBER}1F`, color: "#FF8F00"}}
                    >
                      {FEATURES.find(f => f.name === row.label)?.icon}
                    </div>
                    <div className="flex-1">
                      <div className="text-[11px] font-extrabold leading-tight text-default-800">
                        {row.label}
                      </div>
                      <div
                        className="text-[10px] font-semibold"
                        style={{color: "#FF8F00"}}
                      >
                        {row.opted
                          ? "Already available in dashboard"
                          : "Requested — awaiting approval"}
                      </div>
                    </div>
                    <span
                      className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-white"
                      style={{background: "#FF8F00"}}
                    >
                      {row.opted ? (
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={3.5}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      ) : (
                        <MdOutlineHourglassTop className="h-3 w-3" />
                      )}
                    </span>
                  </div>
                ))}

              <div className="mt-3 flex items-start justify-center">
                {[
                  {
                    label: "Bus Attendance",
                    name: BUS_ATTENDANCE,
                    done: busAdded,
                    opted: !!attendanceContext?.busOpted,
                    requested: !!attendanceContext?.busRequested
                  },
                  {
                    label: "Campus Attendance",
                    name: CAMPUS_ATTENDANCE,
                    done: campusAdded,
                    opted: !!attendanceContext?.campusOpted,
                    requested: !!attendanceContext?.campusRequested
                  }
                ]
                  .sort(
                    (a, b) =>
                      Number(b.opted || b.requested) -
                      Number(a.opted || a.requested)
                  )
                  .concat({
                    label: "Attendance Inconsistency",
                    name: ATTENDANCE_INCONSISTENCY,
                    done: !isLocked,
                    requested: false,
                    opted: false
                  })
                  .map((step, i, arr) => (
                    <Fragment key={step.label}>
                      <div className="flex w-16 flex-col items-center gap-1.5">
                        <div className="relative">
                          <div
                            className="step-icon-fade flex h-8 w-8 items-center justify-center rounded-full"
                            style={{
                              background: step.done ? AMBER : "#EDEEF0",
                              color: step.done ? "#ffffff" : "#9AA0A6",
                              transition: step.done ? undefined : "none",
                              transitionDelay: step.done ? `${i * 0.4}s` : "0s"
                            }}
                          >
                            {FEATURES.find(f => f.name === step.name)?.icon}
                          </div>
                          <span
                            className="step-check-fade absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-green-500 text-white"
                            style={{
                              opacity: step.done ? 1 : 0,
                              transform: step.done ? "scale(1)" : "scale(0)",
                              transition: step.done ? undefined : "none",
                              transitionDelay: step.done ? `${i * 0.6}s` : "0s"
                            }}
                          >
                            <svg
                              width="7"
                              height="7"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={3.5}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                          </span>
                        </div>
                        <span
                          className={`text-center text-[10px] leading-tight ${
                            step.done
                              ? "font-extrabold text-default-800"
                              : "font-semibold text-default-400"
                          }`}
                        >
                          {step.label}
                        </span>
                      </div>
                      {i !== arr.length - 1 && (
                        <div className="mt-4 h-0.5 flex-1 flex-shrink-0 overflow-hidden rounded-full bg-gray-200">
                          <div
                            className="step-line-fill h-full rounded-full bg-green-500"
                            style={{
                              width: busAdded && campusAdded ? "100%" : "0%",
                              transition:
                                busAdded && campusAdded ? undefined : "none",
                              transitionDelay:
                                busAdded && campusAdded
                                  ? `${i * 0.6 + 0.4}s`
                                  : "0s"
                            }}
                          />
                        </div>
                      )}
                    </Fragment>
                  ))}
              </div>

              <div className="mt-3 flex flex-1 flex-col justify-center gap-3">
                {[
                  {
                    label: BUS_ATTENDANCE,
                    done: busAdded,
                    opted: attendanceContext.busOpted,
                    requested: attendanceContext.busRequested
                  },
                  {
                    label: CAMPUS_ATTENDANCE,
                    done: campusAdded,
                    opted: attendanceContext.campusOpted,
                    requested: attendanceContext.campusRequested
                  }
                ]
                  .filter(row => !row.opted && !row.requested)
                  .map(row => (
                    <div
                      key={row.label}
                      className="flex w-full items-center gap-2 rounded-lg border p-2"
                      style={{
                        background: row.done ? `${AMBER}1A` : "#FAFAFA",
                        borderColor: row.done ? `${AMBER}59` : "#EEEEEE"
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          attendanceContext.onJumpToFeature(row.label)
                        }
                        className="flex flex-1 items-center gap-2 text-left"
                      >
                        <div
                          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
                          style={{background: AMBER, color: "#ffffff"}}
                        >
                          {FEATURES.find(f => f.name === row.label)?.icon}
                        </div>
                        <div className="flex-1">
                          <div className="text-[11px] font-extrabold leading-tight text-default-800">
                            {row.label}
                          </div>
                          <div
                            className="text-[10px] font-semibold tracking-wide underline"
                            style={{color: AMBER_TEXT}}
                          >
                            Tap to view
                          </div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          attendanceContext.onToggleFeature(row.label)
                        }
                        className="flex-shrink-0"
                      >
                        {row.done ? (
                          <span className="flex items-center gap-1 rounded-full bg-black px-2.5 py-1.5 text-[10px] font-extrabold text-white">
                            <svg
                              width="9"
                              height="9"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={3.5}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                            Added
                          </span>
                        ) : (
                          <span
                            className="flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[10px] font-extrabold text-black"
                            style={{background: AMBER}}
                          >
                            <svg
                              width="9"
                              height="9"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={3.5}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M12 5v14M5 12h14" />
                            </svg>
                            Add
                          </span>
                        )}
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        <div className="w-full md:max-w-none md:basis-[60%]">
          <div className="mb-1 flex items-center justify-between">
            <span
              className="ml-3 text-xs font-extrabold uppercase tracking-wide"
              style={{color: AMBER_TEXT}}
            >
              {flipped ? "Detail view" : "Dashboard"}
            </span>
            <button
              type="button"
              onClick={() => setFlipped(f => !f)}
              className="mr-2 flex items-center gap-1 rounded-full bg-black px-5 py-1 text-[10px] font-extrabold tracking-wide text-white shadow-sm"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path d="M17 2l4 4-4 4" />
                <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <path d="M7 22l-4-4 4-4" />
                <path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
              Tap to flip
            </button>
          </div>

          <div className="mx-auto w-full max-w-[550px]">
            {!flipped && (
              <div
                key="front"
                className="flip-fade w-full rounded-2xl border border-gray-200 bg-white p-2 shadow-md"
              >
                <div className=" flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="ml-2 text-base font-extrabold text-default-800">
                      Attendance Inconsistency Summary
                    </span>
                    <div className="flex h-3 w-3 items-center justify-center rounded-full bg-slate-400">
                      <svg
                        width="5"
                        height="8"
                        viewBox="0 0 5 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M3.92937 2.66603C4.078 2.40593 4.1616 2.10402 4.1616 1.78354C4.1616 0.798879 3.36272 0 2.37806 0C1.39339 0 0.594514 0.798879 0.594514 1.78354C0.594514 2.76821 1.39339 3.56709 2.37806 3.56709C3.04224 3.56709 3.62282 3.2048 3.92937 2.66603ZM1.18903 4.75611H1.78354H2.97257C3.62979 4.75611 4.1616 5.28793 4.1616 5.94514V7.13417V11.8903C4.1616 12.5475 3.62979 13.0793 2.97257 13.0793C2.31535 13.0793 1.78354 12.5475 1.78354 11.8903V8.02594C1.78354 7.53361 1.3841 7.13417 0.891772 7.13417C0.399439 7.13417 0 6.73473 0 6.2424V5.94514C0 5.52713 0.215976 5.15788 0.541101 4.94654C0.726887 4.82578 0.94983 4.75611 1.18903 4.75611Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                  </div>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    className="text-default-400"
                  >
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </div>
                <p className="mb-1 ml-2 text-[10px] text-default-400">
                  A user may appear in more than one category.
                </p>

                <div className="mb-1 flex divide-x rounded-xl border border-gray-100">
                  <div className="flex flex-1 items-center gap-2 p-1">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 33 33"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M16.5 0C7.3872 0 0 7.3872 0 16.5C0 25.6128 7.3872 33 16.5 33C25.6128 33 33 25.6128 33 16.5C33 7.3872 25.6128 0 16.5 0ZM18.0872 19.5684L18.538 6.47989C18.538 5.49163 17.7999 4.68307 16.8966 4.68307H16.1034C15.2009 4.68307 14.462 5.49163 14.462 6.47989L14.9128 19.5684C14.9128 20.5242 15.6275 21.3061 16.5 21.3061C17.3733 21.3061 18.0872 20.5242 18.0872 19.5684ZM16.5 23.9948C15.3062 23.9948 14.339 24.9629 14.339 26.1559C14.339 27.3497 15.307 28.3169 16.5 28.3169C17.6938 28.3169 18.6618 27.3489 18.6618 26.1559C18.661 24.9629 17.6938 23.9948 16.5 23.9948Z"
                          fill="#EF4444"
                        />
                      </svg>
                    </div>
                    <div>
                      <div className=" font-extrabold text-red-500">150</div>
                      <div className="text-[10px] leading-tight text-default-400">
                        Total Inconsistencies
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-1 items-center gap-2 p-2">
                    <div
                      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
                      style={{background: "#FDF3D9"}}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={AMBER_TEXT}
                        strokeWidth={2}
                      >
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-extrabold text-red-500">
                        150
                        <span className="text-xs text-default-600">/250</span>
                      </div>
                      <div className="text-[10px] leading-tight text-default-400">
                        Affected User
                      </div>
                    </div>
                  </div>
                </div>

                <div className="divide-y divide-gray-100">
                  {[
                    {
                      label: "Boarded Bus - Not Deboarded at School",
                      value: 9,
                      pct: "6%",
                      barColor: "bg-blue-400",
                      bg: "#EAF1FF",
                      color: "#3E7BFA",
                      icon: <FaBusAlt className="h-4 w-4" />
                    },
                    {
                      label: "Deboarded at School - No School Entry",
                      value: 8,
                      pct: "5.33%",
                      barColor: "bg-amber-300",
                      bg: "#FDF3D9",
                      color: AMBER_TEXT,
                      icon: <RiDoorOpenLine className="h-4 w-4" />
                    },
                    {
                      label: "School Entry Found - No Morning Bus Record",
                      value: 28,
                      pct: "5.33%",
                      barColor: "bg-green-300",
                      bg: "#E6F7EE",
                      color: "#0FA36B",
                      icon: <TbDoorEnter className="h-4 w-4" />
                    },
                    {
                      label: "Entered School - No School Exit",
                      value: 29,
                      pct: "19.33%",
                      barColor: "bg-purple-300",
                      bg: "#F1EBFB",
                      color: "#8A5CD6",
                      icon: <FaSchoolCircleExclamation className="h-4 w-4" />
                    },
                    {
                      label: "Exited School - Not Boarded Evening Bus",
                      value: 35,
                      pct: "23.33%",
                      barColor: "bg-red-300",
                      bg: "#FDECEC",
                      color: "#EF4444",
                      icon: <RxExit className="h-4 w-4" />
                    },
                    {
                      label: "Boarded Evening Bus - Not Deboarded",
                      value: 31,
                      pct: "20.67%",
                      barColor: "bg-sky-200",
                      bg: "#E0F2FE",
                      color: "#0284C7",
                      icon: <FaBusAlt className="h-3.5 w-3.5" />
                    }
                  ].map(row => (
                    <div
                      key={row.label}
                      className="flex items-center gap-3 px-3 py-1 text-[10px]"
                    >
                      <div
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
                        style={{background: row.bg, color: row.color}}
                      >
                        {row.icon}
                      </div>
                      <span className="flex-1 text-[11px]  leading-tight text-default-700">
                        {row.label}
                      </span>
                      <span className="w-6 text-right font-bold text-default-800">
                        {row.value}
                      </span>
                      <div className="h-1 w-10 overflow-hidden rounded-full bg-gray-100">
                        <div
                          className={`h-full rounded-full ${row.barColor}`}
                          style={{
                            width: `${Math.min(100, parseFloat(row.pct))}%`
                          }}
                        />
                      </div>
                      <span className="w-12 text-right text-[10px] text-default-400">
                        {row.pct}
                      </span>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                        className="text-default-400"
                      >
                        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Back: Attendance Inconsistency detail view */}
            {flipped && (
              <div
                key="back"
                className="flip-fade overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-md"
              >
                <div className="mb-1 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      className="text-default-500"
                    >
                      <path d="M4 6h16M4 12h10M4 18h6" />
                    </svg>
                    <span className="text-base font-bold text-default-800">
                      Attendance Inconsistency Summary
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 rounded-full border border-gray-200 px-2.5 py-1 text-[10px] text-default-400">
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <circle cx="11" cy="11" r="7" />
                        <path d="m21 21-4.35-4.35" />
                      </svg>
                      Search..
                    </div>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      className="text-default-400"
                    >
                      <path d="M12 15V3m0 12-4-4m4 4 4-4M4 21h16" />
                    </svg>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      className="text-default-400"
                    >
                      <rect x="3" y="3" width="7" height="7" rx="1" />
                      <rect x="14" y="3" width="7" height="7" rx="1" />
                      <rect x="3" y="14" width="7" height="7" rx="1" />
                      <rect x="14" y="14" width="7" height="7" rx="1" />
                    </svg>
                  </div>
                </div>
                <div className="mb-3 text-[10px] font-semibold text-default-400">
                  Total Data : 151
                </div>

                <div className="rounded-lg border border-gray-100">
                  <div>
                    <div className="grid grid-cols-[0.9fr_1.3fr_0.5fr_1fr_0.9fr_0.9fr] gap-1 border-b border-gray-100 bg-gray-50 px-2 py-1.5 text-[8px] font-bold text-default-500">
                      <span>Date</span>
                      <span>Name</span>
                      <span>Class</span>
                      <span>Admission No</span>
                      <span>Pickup Time</span>
                      <span>Drop Time</span>
                    </div>
                    {[
                      {
                        name: "Aadhav Krishna",
                        cls: "7",
                        adm: "AD001",
                        pickup: "07:22 am",
                        drop: "3:44 pm"
                      },
                      {
                        name: "Aadhira Sree S P",
                        cls: "3",
                        adm: "AD002",
                        pickup: "07:50 am",
                        drop: "3:43 pm"
                      },
                      {
                        name: "Aadhira  S P",
                        cls: "3",
                        adm: "AD003",
                        pickup: "07:50 am",
                        drop: "3:43 pm"
                      },
                      {
                        name: "Aadhirashri M S",
                        cls: "7",
                        adm: "AD004",
                        pickup: "06:42 am",
                        drop: "3:50 pm"
                      },
                      {
                        name: "Aadhya Saravanakumar",
                        cls: "10",
                        adm: "AD005",
                        pickup: "07:24 am",
                        drop: "3:34 pm"
                      },
                      {
                        name: "Aadhira Sree S P",
                        cls: "3",
                        adm: "AD006",
                        pickup: "07:50 am",
                        drop: "3:43 pm"
                      },
                      {
                        name: "Aadhira Sree S P",
                        cls: "3",
                        adm: "AD007",
                        pickup: "07:50 am",
                        drop: "3:43 pm"
                      },
                      {
                        name: "Aadhira Sree S P",
                        cls: "3",
                        adm: "AD008",
                        pickup: "07:50 am",
                        drop: "3:43 pm"
                      }
                    ].map((row, i, arr) => (
                      <div
                        key={row.adm}
                        className={`grid grid-cols-[0.9fr_1.3fr_0.5fr_1fr_0.9fr_0.9fr] items-center gap-1 px-2 py-2 text-[9px] ${
                          i !== arr.length - 1 ? "border-b border-gray-50" : ""
                        }`}
                      >
                        <span className="font-semibold text-default-800">
                          {todayLabel}
                        </span>
                        <span className="font-semibold text-default-800">
                          {mappedUserDetails[i] ?? row.name}
                        </span>
                        <span className="text-default-700">{row.cls}</span>
                        <span className="text-default-700">{row.adm}</span>
                        <span className="text-default-700">{row.pickup}</span>
                        <span className="text-default-700">{row.drop}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <style jsx>{`
          .step-check-fade {
            transition:
              opacity 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
              transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          }
          .step-icon-fade {
            transition:
              background 0.2s ease,
              color 0.2s ease;
          }
          .step-line-fill {
            transition: width 0.2s linear;
          }
          @keyframes flip-fade {
            0% {
              opacity: 0;
            }
            100% {
              opacity: 1;
            }
          }
          .flip-fade {
            animation: flip-fade 0.25s ease-in;
          }
        `}</style>
      </div>
    );
  }
  // Fuel Summary
  return (
    <div className="flex flex-col gap-3 px-4 md:flex-row md:items-start">
      <div className="w-full max-w-[550px] md:flex-1">
        <div
          className="mb-1.5 text-xs font-extrabold uppercase tracking-wide"
          style={{color: AMBER_TEXT}}
        >
          Dashboard
        </div>
        <div className="w-full rounded-2xl  border  border-gray-200 bg-white p-4 shadow-md">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-lg font-bold text-default-800">
              Fuel Theft & Refilling
            </span>
            <div className="flex items-center gap-3 text-default-400">
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
              </svg>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </div>
          </div>

          <div className="mb-3 flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2.5 text-xs font-semibold text-default-700">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            <span className="flex-1">Last 7 Days</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </div>

          <div className="mb-3 flex gap-2">
            <div className="flex-1 rounded-xl border border-gray-100 p-3 text-center">
              <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-500">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path d="M12 2s7 8 7 13a7 7 0 0 1-14 0c0-5 7-13 7-13Z" />
                </svg>
              </div>
              <div className="text-xs font-extrabold text-default-900">
                424.94{" "}
                <span className="text-xs font-semibold text-default-400">
                  Litres
                </span>
              </div>
              <div className="mb-2 text-xs text-default-400">
                Fuel Refilling
              </div>
              <div className="mx-auto flex w-fit items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-semibold text-blue-500">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.55007 0C14.5639 0 17.9064 1.31313 17.9064 2.9844V3.58127V4.77503C18.5667 4.77503 19.1001 5.30849 19.1001 5.96879V8.35631C19.1001 9.01661 18.5667 9.55007 17.9064 9.55007V15.5189C17.9064 16.1792 17.3729 16.7126 16.7126 16.7126V17.9064C16.7126 18.5667 16.1792 19.1001 15.5189 19.1001H14.3251C13.6648 19.1001 13.1313 18.5667 13.1313 17.9064V16.7126H5.96879V17.9064C5.96879 18.5667 5.43533 19.1001 4.77503 19.1001H3.58127C2.92098 19.1001 2.38752 18.5667 2.38752 17.9064V16.7126C1.72722 16.7126 1.19376 16.1792 1.19376 15.5189V9.55007C0.533461 9.55007 0 9.01661 0 8.35631V5.96879C0 5.30849 0.533461 4.77503 1.19376 4.77503V3.58127V2.9844C1.19376 1.31313 4.53628 0 9.55007 0ZM3.58127 5.96879V9.55007C3.58127 10.2104 4.11474 10.7438 4.77503 10.7438H8.95319V4.77503H4.77503C4.11474 4.77503 3.58127 5.30849 3.58127 5.96879ZM10.1469 10.7438H14.3251C14.9854 10.7438 15.5189 10.2104 15.5189 9.55007V5.96879C15.5189 5.30849 14.9854 4.77503 14.3251 4.77503H10.1469V10.7438ZM4.17815 14.922C4.49476 14.922 4.7984 14.7962 5.02227 14.5723C5.24614 14.3485 5.37191 14.0448 5.37191 13.7282C5.37191 13.4116 5.24614 13.108 5.02227 12.8841C4.7984 12.6602 4.49476 12.5345 4.17815 12.5345C3.86155 12.5345 3.55791 12.6602 3.33404 12.8841C3.11017 13.108 2.9844 13.4116 2.9844 13.7282C2.9844 14.0448 3.11017 14.3485 3.33404 14.5723C3.55791 14.7962 3.86155 14.922 4.17815 14.922ZM14.922 14.922C15.2386 14.922 15.5422 14.7962 15.7661 14.5723C15.99 14.3485 16.1157 14.0448 16.1157 13.7282C16.1157 13.4116 15.99 13.108 15.7661 12.8841C15.5422 12.6602 15.2386 12.5345 14.922 12.5345C14.6054 12.5345 14.3017 12.6602 14.0779 12.8841C13.854 13.108 13.7282 13.4116 13.7282 13.7282C13.7282 14.0448 13.854 14.3485 14.0779 14.5723C14.3017 14.7962 14.6054 14.922 14.922 14.922ZM13.1313 2.9844C13.1313 2.65611 12.8627 2.38752 12.5345 2.38752H6.56567C6.23739 2.38752 5.96879 2.65611 5.96879 2.9844C5.96879 3.31268 6.23739 3.58127 6.56567 3.58127H12.5345C12.8627 3.58127 13.1313 3.31268 13.1313 2.9844Z"
                    fill="#A3AED0"
                  />
                </svg>
                3/6 vehicles
              </div>
            </div>
            <div className="flex-1 rounded-xl border border-gray-100 p-3 text-center">
              <div
                className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl"
                style={{background: "#FDF3D9"}}
              >
                <svg
                  width="23"
                  height="20"
                  viewBox="0 0 32 30"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M31.7286 12.4135C31.7286 12.2663 31.6913 12.1211 31.621 11.9923L26.2035 2.05409C25.9694 1.62383 25.428 1.46427 24.9925 1.69601C24.5586 1.92899 24.3973 2.46671 24.6314 2.89697L26.0054 5.41781L24.1959 8.55185C24.073 8.76432 24.0441 9.01657 24.116 9.25036C24.188 9.48415 24.3535 9.67815 24.5747 9.78644L29.7792 12.3401L29.9418 12.6383V19.9158C29.9418 20.9916 29.0595 21.8665 27.9754 21.8665H27.0345C25.9504 21.8665 25.069 20.9916 25.069 19.9158V15.1924C25.069 13.6407 23.7959 12.3782 22.2312 12.3782H20.906V3.13937C20.906 1.40726 19.4865 0 17.741 0H6.07306C4.32638 0 2.90728 1.40767 2.90728 3.13937V28.0903H0.893401C0.399963 28.0903 0 28.4877 0 28.9762C0 29.4647 0.399963 29.8622 0.893401 29.8622H22.4885C22.9811 29.8622 23.3811 29.4647 23.3811 28.9762C23.3811 28.4877 22.9811 28.0903 22.4885 28.0903H20.906V14.1501H22.2308C22.8099 14.1501 23.2818 14.6181 23.2818 15.1924V19.9158C23.2818 21.969 24.9652 23.6384 27.0341 23.6384H27.975C30.0447 23.6384 31.7281 21.969 31.7281 19.9158L31.7286 12.4135ZM26.218 8.61584L27.0055 7.25164L28.3076 9.64124L26.218 8.61584ZM13.8775 22.8123C13.3572 23.4021 12.6561 23.7266 11.9062 23.7266C10.3924 23.7266 9.15943 22.3743 9.15943 20.7135C9.16026 19.2571 11.0132 16.8273 11.3842 16.3556C11.6345 16.0385 12.1767 16.0352 12.4278 16.3556C12.7992 16.8273 14.6526 19.2579 14.6526 20.7135C14.653 21.501 14.3784 22.2471 13.8775 22.8123ZM16.4878 10.1593C16.4878 11.7638 15.1717 13.0698 13.5545 13.0698H9.68017C8.06294 13.0698 6.74601 11.7638 6.74601 10.1593V6.88906C6.74601 5.28451 8.06294 3.97856 9.68017 3.97856H13.5545C15.1717 3.97856 16.4878 5.28451 16.4878 6.88906V10.1593Z"
                    fill="#F28A03"
                  />
                </svg>
              </div>
              <div className="text-xs font-extrabold text-default-900">3/6</div>
              <div className="mb-2 text-xs text-default-400">
                Low Fuel Vehicles
              </div>
              <div
                className="mx-auto flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold"
                style={{background: "amber-100", color: AMBER_TEXT}}
              >
                <svg
                  width="8"
                  height="8"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <circle cx="12" cy="12" r="10" />
                </svg>
                Limit 30 %
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-gray-100 p-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-500">
              <svg
                width="20"
                height="17"
                viewBox="0 0 37 33"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M36.4713 27.0747L22.0272 2.01639C21.6663 1.40247 21.151 0.893441 20.5324 0.539753C19.9139 0.186065 19.2135 0 18.5008 0C17.7881 0 17.0878 0.186065 16.4692 0.539753C15.8507 0.893441 15.3354 1.40247 14.9744 2.01639L0.530316 27.0747C0.183023 27.6685 0 28.3439 0 29.0316C0 29.7193 0.183023 30.3947 0.530316 30.9885C0.886638 31.6061 1.40104 32.1179 2.02075 32.4713C2.64045 32.8248 3.34314 33.0072 4.0567 32.9998H32.945C33.658 33.0066 34.36 32.8239 34.979 32.4705C35.5981 32.1171 36.112 31.6056 36.468 30.9885C36.8158 30.3949 36.9994 29.7197 37 29.032C37.0006 28.3443 36.8181 27.6688 36.4713 27.0747ZM34.1804 29.6668C34.0545 29.8814 33.8737 30.0587 33.6566 30.1804C33.4395 30.3022 33.1939 30.3641 32.945 30.3598H4.0567C3.80775 30.3641 3.56213 30.3022 3.34503 30.1804C3.12793 30.0587 2.94715 29.8814 2.82123 29.6668C2.70717 29.4739 2.647 29.254 2.647 29.0299C2.647 28.8059 2.70717 28.5859 2.82123 28.393L17.2654 3.33473C17.3938 3.12117 17.5754 2.94446 17.7925 2.82178C18.0096 2.6991 18.2547 2.63463 18.5041 2.63463C18.7535 2.63463 18.9987 2.6991 19.2158 2.82178C19.4328 2.94446 19.6144 3.12117 19.7429 3.33473L34.187 28.393C34.3001 28.5865 34.3591 28.8068 34.358 29.0308C34.3568 29.2549 34.2955 29.4745 34.1804 29.6668ZM17.1795 19.7999V13.2C17.1795 12.8499 17.3187 12.5142 17.5665 12.2666C17.8143 12.0191 18.1504 11.88 18.5008 11.88C18.8513 11.88 19.1874 12.0191 19.4352 12.2666C19.683 12.5142 19.8222 12.8499 19.8222 13.2V19.7999C19.8222 20.15 19.683 20.4857 19.4352 20.7333C19.1874 20.9808 18.8513 21.1199 18.5008 21.1199C18.1504 21.1199 17.8143 20.9808 17.5665 20.7333C17.3187 20.4857 17.1795 20.15 17.1795 19.7999ZM20.4829 25.7399C20.4829 26.1315 20.3666 26.5143 20.1488 26.8399C19.931 27.1655 19.6215 27.4193 19.2593 27.5691C18.8971 27.719 18.4986 27.7582 18.1141 27.6818C17.7297 27.6054 17.3765 27.4168 17.0993 27.1399C16.8221 26.863 16.6333 26.5102 16.5569 26.1261C16.4804 25.7421 16.5196 25.3439 16.6697 24.9822C16.8197 24.6204 17.0737 24.3111 17.3997 24.0936C17.7256 23.876 18.1088 23.7599 18.5008 23.7599C19.0265 23.7599 19.5306 23.9685 19.9023 24.3398C20.274 24.7111 20.4829 25.2147 20.4829 25.7399Z"
                  fill="#FC2A3D"
                />
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-xs font-bold text-default-800">
                Unusual fuel drop detected!
              </div>
              <div className="text-xs text-default-400">2/6 Vehicles</div>
            </div>
            <div className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-red-50 px-3 py-1.5 text-xs font-bold text-red-500">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="10" />
              </svg>
              Fuel Theft
            </div>
          </div>
        </div>
      </div>

      {/* Image 2: Fuel Filling & Theft detail view */}
      <div className="w-full max-w-[550px] md:flex-1">
        <div
          className="mb-1.5 text-xs font-extrabold uppercase tracking-wide"
          style={{color: AMBER_TEXT}}
        >
          Detail view
        </div>
        <div className="overflow-hidden rounded-2xl border  border-gray-200 bg-white p-4 shadow-md">
          <div className="mb-1 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="text-default-500"
              >
                <path d="M4 6h16M4 12h10M4 18h6" />
              </svg>
              <span className="text-base font-bold text-default-800">
                Fuel Filling & Theft
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              {["All", "Fuel Fill", "Low Fuel", "Fuel Theft", "No Data"].map(
                (tab, i) => (
                  <span
                    key={tab}
                    className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[9px] font-bold ${
                      i === 0 ? "bg-blue-500 text-white" : "text-default-400"
                    }`}
                  >
                    {tab}
                  </span>
                )
              )}
            </div>
          </div>
          <div className="mb-1 text-[10px] font-semibold text-default-400">
            Total Data : 6
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-100">
            <div className="min-w-[640px]">
              <div className="grid grid-cols-[1fr_0.8fr_1fr_0.9fr_0.9fr_0.8fr_1fr] gap-1 border-b border-gray-100 bg-gray-50 px-2 py-1.5 text-[10px] font-bold text-default-500">
                <span>Vehicle Name</span>
                <span>Status</span>
                <span>Refill Date & Time</span>
                <span>Fuel Qty (L)</span>
                <span>Theft Detected On</span>
                <span>Fuel Loss (L)</span>
                <span>Location</span>
              </div>
              {[
                {
                  vehicle: "09_TN29 AQ 1937",
                  status: "Fuel Fill",
                  date: randomRecentDateTime(14, 30),
                  qty: "159.77",
                  theft: "",
                  loss: "",
                  location: ""
                },
                {
                  vehicle: "09_TN29 AQ 1587",
                  status: "Fuel Fill",
                  date: randomRecentDateTime(9, 40),
                  qty: "159.77",
                  theft: "",
                  loss: "",
                  location: ""
                },
                {
                  vehicle: "09_TN29 AQ 1577",
                  status: "Fuel Fill",
                  date: randomRecentDateTime(18, 0),
                  qty: "159.77",
                  theft: "",
                  loss: "",
                  location: ""
                },
                {
                  vehicle: "06_TN29 AM 6027",
                  status: "Low Fuel",
                  date: "",
                  qty: "",
                  theft: "",
                  loss: "",
                  location: ""
                },
                {
                  vehicle: "12_TN29 AQ 2054",
                  status: "Fuel theft",
                  date: "",
                  qty: "",
                  theft: randomRecentDateTime(7, 0),
                  loss: "3.05",
                  location: "View Location"
                },
                {
                  vehicle: "12_TN29 AQ 2054",
                  status: "Fuel theft",
                  date: "",
                  qty: "",
                  theft: randomRecentDateTime(7, 3),
                  loss: "3.84",
                  location: "View Location"
                }
              ].map((row, i, arr) => {
                const statusStyle: Record<string, string> = {
                  "Fuel Fill": "bg-green-50 text-green-600",
                  "Low Fuel": "bg-amber-50 text-amber-500",
                  "Fuel theft": "bg-red-50 text-red-500"
                };
                return (
                  <div
                    key={`${row.vehicle}-${i}`}
                    className={`grid grid-cols-[1fr_0.8fr_1fr_0.9fr_0.9fr_0.8fr_1fr] items-center gap-1 px-2 py-2 text-[10px] ${
                      i !== arr.length - 1 ? "border-b border-gray-50" : ""
                    }`}
                  >
                    <span className="font-semibold text-default-800">
                      {mappedVehicleDetails[i] ?? row.vehicle}
                    </span>
                    <span
                      className={`w-fit whitespace-nowrap rounded-full px-1.5 py-0.5 text-[8px] font-bold ${statusStyle[row.status]}`}
                    >
                      {row.status}
                    </span>
                    <span className="text-default-700">{row.date || "-"}</span>
                    <span className="text-default-700">{row.qty || "-"}</span>
                    <span className="text-default-700">{row.theft || "-"}</span>
                    <span className="text-default-700">{row.loss || "-"}</span>
                    <span className="font-semibold text-blue-500">
                      {row.location || "-"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export interface AttendanceInconsistencyContext {
  busAdded: boolean;
  campusAdded: boolean;
  busOpted: boolean;
  campusOpted: boolean;
  busRequested: boolean;
  campusRequested: boolean;
  locked: boolean;
  onJumpToFeature: (name: string) => void;
  onToggleFeature: (name: string) => void;
}

export interface PreviewModalProps {
  feature: FeatureMeta;
  index: number;
  total: number;
  added: boolean;
  direction?: "next" | "prev";
  locked?: boolean;
  lockedMessage?: string;
  onClose: () => void;
  onJump: (i: number) => void;
  onToggleAdd: () => void;
  onNext: () => void;
  onReview: () => void;
  attendanceContext?: AttendanceInconsistencyContext;
  mappedDetails?: any;
}

export const PreviewModal: React.FC<PreviewModalProps> = ({
  feature,
  index,
  total,
  added,
  direction = "next",
  locked = false,
  lockedMessage,
  onJump,
  onToggleAdd,
  onNext,
  onReview,
  attendanceContext,
  mappedDetails
}) => {
  const slideAnimation =
    direction === "prev"
      ? "missing-feature-slide-left"
      : "missing-feature-slide-right";
  const isLast = index === total - 1;
  const [justAdded, setJustAdded] = useState(false);
  const advance = isLast ? onReview : onNext;
  const isAddOn = feature.name === ATTENDANCE_INCONSISTENCY;

  const handleAdd = () => {
    if (locked) return;
    if (added) {
      // Already added - clicking again unselects it instead of re-advancing.
      onToggleAdd();
      return;
    }
    if (justAdded) return;
    onToggleAdd();
    setJustAdded(true);
    setTimeout(() => {
      setJustAdded(false);
      advance();
    }, 350);
  };

  const handleSkip = () => {
    advance();
  };

  const showAdded = added || justAdded;
  return (
    <>
      <div
        className="relative -m-4 flex w-[calc(100%+2rem)] flex-col overflow-hidden shadow-2xl"
        style={{
          height: "95vh",
          background:
            "linear-gradient(180deg, #FFFFFF 0%, #FFF7E9 55%, #FDEAC5 100%)"
        }}
      >
        <div className="flex-shrink-0 px-4 pb-2 pt-8">
          <h2 className="flex items-center gap-2 text-xl font-extrabold text-default-900">
            {feature.name}
          </h2>
        </div>

        <div
          key={`slide-content-${feature.name}`}
          className={`flex-1 overflow-y-auto px-4 pb-1 ${slideAnimation}`}
        >
          <PreviewSlideContent
            name={feature.name}
            attendanceContext={attendanceContext}
            mappedDetails={mappedDetails}
          />
        </div>

        <div className="flex-shrink-0 px-4 pb-5 pt-4">
          <div className="px-1 pb-1 text-center">
            <div
              className="mb-2 text-xs font-extrabold tracking-wide"
              style={{color: "#FF8F00"}}
            >
              &bull; {feature.eyebrow}
            </div>
            <h3 className="mb-2.5 text-lg font-extrabold leading-snug">
              {feature.headline}
            </h3>
            <p className="mb-3 text-sm leading-relaxed text-default-700">
              {feature.sub}
            </p>
            <div className="mb-4 flex items-center justify-center gap-3">
              <button
                className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white text-default-500 shadow-sm transition-opacity disabled:cursor-not-allowed disabled:opacity-30"
                onClick={() => onJump(index - 1)}
                disabled={index === 0}
                aria-label="Previous"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex items-center gap-1.5">
                {Array.from({length: total}).map((_, i) => (
                  <div
                    key={i}
                    className="h-1.5 cursor-pointer rounded-full transition-all"
                    style={{
                      width: i === index ? 20 : 7,
                      background: i === index ? "#111214" : "#E3D6BC"
                    }}
                    onClick={() => onJump(i)}
                  />
                ))}
              </div>
              <button
                className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white text-default-500 shadow-sm transition-opacity disabled:cursor-not-allowed disabled:opacity-30"
                onClick={() => onJump(index + 1)}
                disabled={index === total - 1}
                aria-label="Next"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {isAddOn ? (
            <div className="flex justify-center">
              {attendanceContext?.busAdded && attendanceContext?.campusAdded ? (
                <button
                  className="flex w-[200px] flex-shrink-0 items-center justify-center gap-2 rounded-full bg-black px-5 py-3.5 text-sm font-extrabold text-white"
                  onClick={advance}
                >
                  Next
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </button>
              ) : (
                <button
                  className="flex w-[200px] flex-shrink-0 items-center justify-center gap-2 rounded-full bg-gray-50 px-4 py-3.5 text-sm font-extrabold"
                  onClick={handleSkip}
                >
                  {isLast ? "Skip & Review" : "Skip"}
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
          ) : (
            <div className="flex justify-center gap-2.5">
              <button
                className={`flex w-[200px] flex-shrink-0 items-center justify-center gap-1.5 rounded-full px-5 py-3.5 text-sm font-extrabold disabled:cursor-not-allowed disabled:opacity-40 ${
                  showAdded ? "bg-black text-white" : "text-default-800"
                }`}
                style={showAdded ? undefined : {background: AMBER}}
                onClick={handleAdd}
                disabled={locked && !added}
              >
                {showAdded ? (
                  <>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={3}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                    Added
                  </>
                ) : (
                  "+ Add"
                )}
              </button>
              <button
                className="flex w-[200px] flex-shrink-0 items-center justify-center gap-2 rounded-full bg-gray-50 px-4 py-3.5 text-sm font-extrabold"
                onClick={handleSkip}
              >
                {isLast ? "Skip & Review" : "Skip"}
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
      <style jsx>{`
        @keyframes missing-feature-slide-right {
          0% {
            opacity: 0;
            transform: translateX(24px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes missing-feature-slide-left {
          0% {
            opacity: 0;
            transform: translateX(-24px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .missing-feature-slide-right {
          animation: missing-feature-slide-right 0.3s ease-out;
        }
        .missing-feature-slide-left {
          animation: missing-feature-slide-left 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export interface AllRequestsModalProps {
  features: FeatureMeta[];
  requestedFeatures?: FeatureMeta[];
  selected?: Record<string, boolean>;
  attendanceInconsistencyLocked?: boolean;
  attendanceInconsistencyLockedMessage?: string;
  submitted: boolean;
  onSubmit: () => void;
  onPreviewRequest: () => void;
  onClose: () => void;
}

export const AllRequestsModal: React.FC<AllRequestsModalProps> = ({
  features,
  requestedFeatures = [],
  selected = {},
  attendanceInconsistencyLocked = false,
  attendanceInconsistencyLockedMessage,
  submitted,
  onSubmit,
  onPreviewRequest,
  onClose
}) => {
  return (
    <div
      className="relative -m-4 w-[calc(100%+2rem)] overflow-y-auto rounded-[20px] bg-white p-6 pt-7"
      style={{maxHeight: "88vh"}}
      role="dialog"
      aria-modal="true"
    >
      {!submitted ? (
        <>
          <button
            className="h-7.5 w-7.5 absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-sm text-default-500 hover:bg-gray-200"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
          <h2 className="mx-auto mb-2 text-center text-lg font-extrabold leading-snug">
            {features.length === 1 ? (
              <>
                Upgrade your dashboard with a new{" "}
                <span style={{color: AMBER_TEXT}}>feature</span>
              </>
            ) : (
              <>
                Upgrade your dashboard with more{" "}
                <span style={{color: AMBER_TEXT}}>features</span>
              </>
            )}
          </h2>
          <p className="mx-auto mb-4  text-center text-xs font-medium  leading-relaxed text-default-500">
            {features.length === 0 && requestedFeatures.length > 0
              ? "Every feature has been requested — sit tight for approval."
              : "Here's what you've requested and what's still available."}
          </p>
          {features.length === 0 && requestedFeatures.length > 0 ? (
            <div className="mb-4 rounded-2xl p-3">
              <h4 className="mb-1.5 px-1 text-xs font-extrabold uppercase tracking-wide">
                Requested Feature{requestedFeatures.length === 1 ? "" : "s"}
              </h4>
              <div>
                {requestedFeatures.map(f => (
                  <div
                    key={f.name}
                    className={`flex items-center gap-3 py-3 ${
                      f.name === "Fuel Theft & Refilling" ? "border-t" : ""
                    }`}
                  >
                    <div
                      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
                      style={{background: `${AMBER}29`, color: AMBER_TEXT}}
                    >
                      {f.icon}
                    </div>
                    <div className="flex-1">
                      <b className="mb-0.5 flex items-center gap-1.5 text-sm font-semibold">
                        {f.name}
                      </b>
                      <span className="block text-xs leading-relaxed text-default-600">
                        {f.desc}
                      </span>
                    </div>
                    <div className="flex flex-shrink-0 flex-col items-center gap-0.5">
                      <MdOutlineHourglassTop className="text-[#FF8F00]" />
                      <span
                        className="whitespace-nowrap text-[10px] font-bold"
                        style={{color: "#FF8F00"}}
                      >
                        Requested
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            requestedFeatures.length > 0 && (
              <div className="mb-4">
                <h4 className="mb-1.5 text-sm font-extrabold uppercase tracking-wide ">
                  Requested
                </h4>
                <div>
                  {requestedFeatures.map(f => (
                    <div
                      key={f.name}
                      className="flex items-center gap-3.5 border-b py-3 last:border-b-0"
                    >
                      <div className="flex w-5 flex-shrink-0 justify-center text-default-800">
                        {f.icon}
                      </div>
                      <div className="flex-1">
                        <b className="mb-0.5 flex items-center gap-1.5 text-xs font-semibold">
                          {f.name}
                        </b>
                        <span className="block text-xs leading-relaxed text-default-600">
                          {f.desc}
                        </span>
                      </div>
                      <span
                        className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md"
                        style={{background: "#FDF3D9"}}
                        title="Requested"
                      >
                        <MdOutlineHourglassTop className="text-[#FF8F00]" />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
          {features.length > 0 && (
            <div className="mb-4">
              <h4 className="mb-1.5 text-sm font-extrabold uppercase tracking-wide">
                Feature you can Unlock
              </h4>
              <div>
                {features.map(f => {
                  const checked = !!selected[f.name];
                  const isAttendanceInconsistency =
                    f.name === ATTENDANCE_INCONSISTENCY;
                  const isLockedAddOn =
                    isAttendanceInconsistency && attendanceInconsistencyLocked;
                  return (
                    <div
                      key={f.name}
                      className="flex items-center gap-3.5 border-b py-3.5 last:border-b-0"
                    >
                      <div className="flex w-5 flex-shrink-0 justify-center text-default-800">
                        {f.icon}
                      </div>
                      <div className="flex-1">
                        <b className="mb-0.5 flex items-center gap-1.5 text-xs font-bold text-default-700">
                          {f.name}
                        </b>
                        <span className="block text-xs leading-relaxed text-default-400">
                          {f.desc}
                        </span>
                      </div>
                      {isLockedAddOn ? (
                        <span
                          className="flex flex-shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold"
                          style={{
                            background: `${AMBER}1A`,
                            borderColor: `${AMBER}59`,
                            color: "#FFCA28"
                          }}
                        >
                          <BsLightningChargeFill className="h-3 w-3" />
                          Explore
                        </span>
                      ) : checked ? (
                        isAttendanceInconsistency ? (
                          <span className="flex flex-shrink-0 items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-[10px] font-bold text-green-600">
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={3}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                            Add-on Feature
                          </span>
                        ) : (
                          <span className="flex flex-shrink-0 items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-[10px] font-bold text-green-600">
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={3}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                            Selected
                          </span>
                        )
                      ) : (
                        <MdLockOutline className="h-5 w-5 flex-shrink-0 text-default-300" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {features.length > 0 &&
            (features.some(f => selected[f.name]) ? (
              <button
                className="mt-4 w-full rounded-full py-4 text-sm font-extrabold text-black/90"
                style={{background: AMBER, color: "#1A1300"}}
                onClick={onSubmit}
              >
                Submit request
              </button>
            ) : (
              <button
                className="mt-4 w-full rounded-full py-4 text-sm font-extrabold text-black/90"
                style={{background: AMBER, color: "#1A1300"}}
                onClick={onPreviewRequest}
              >
                Preview & Request
              </button>
            ))}
        </>
      ) : (
        <div className="flex flex-col items-center px-4 py-8 text-center">
          <div
            className="mb-5 flex h-[74px] w-[74px] items-center justify-center rounded-full"
            style={{background: AMBER, boxShadow: `0 0 0 8px ${"#F1F2F4"}`}}
          >
            <svg
              width="30"
              height="30"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth={3}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <h2 className="mb-2.5 text-lg font-extrabold">Request submitted</h2>
          <p className="mb-6 max-w-[270px] text-xs leading-relaxed text-default-500">
            Our customer support has been notified, they will reach out to you.
          </p>
          <button
            className="w-full rounded-full bg-black py-4 text-sm font-extrabold text-white hover:bg-black/90"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
};

export interface ReviewModalProps {
  features: FeatureMeta[];
  requestedFeatures?: FeatureMeta[];
  selected: Record<string, boolean>;
  allChecked: boolean;
  submitted: boolean;
  attendanceInconsistencyLocked?: boolean;
  attendanceInconsistencyLockedMessage?: string;
  onToggleFeature: (name: string) => void;
  onToggleAll: () => void;
  onSubmit: () => void;
  onClose: () => void;
}

export const ReviewModal: React.FC<ReviewModalProps> = ({
  features,
  requestedFeatures = [],
  selected,
  allChecked,
  submitted,
  attendanceInconsistencyLocked = false,
  attendanceInconsistencyLockedMessage,
  onToggleFeature,
  onToggleAll,
  onSubmit,
  onClose
}) => {
  const anySelected = features.some(f => selected[f.name]);
  return (
    <div
      className="relative -m-4 w-[calc(100%+2rem)] overflow-y-auto rounded-[20px] bg-white p-6 pt-7"
      style={{maxHeight: "88vh"}}
      role="dialog"
      aria-modal="true"
    >
      {!submitted ? (
        <>
          <button
            className="h-7.5 w-7.5 absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-sm text-default-500 hover:bg-gray-200"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
          <h2 className="mx-auto mb-2  text-center text-lg font-extrabold leading-snug">
            Ready to <span style={{color: AMBER_TEXT}}>level up</span> your
            dashboard?
          </h2>
          <p className="mx-auto mb-4  text-center text-xs font-semibold leading-relaxed text-default-500">
            Review what you picked while previewing - add or remove before you
            submit.
          </p>
          {features.length > 0 && (
            <div className="mb-1.5 flex items-center justify-end">
              <button
                className="text-xs font-bold text-green-600 hover:underline"
                onClick={onToggleAll}
              >
                {allChecked ? "Deselect all" : "Select all"}
              </button>
            </div>
          )}
          {features.length > 0 && (
            <>
              <div>
                {features.map(f => {
                  const checked = !!selected[f.name];
                  const isAttendanceInconsistency =
                    f.name === ATTENDANCE_INCONSISTENCY;
                  const isLockedAddOn =
                    isAttendanceInconsistency && attendanceInconsistencyLocked;
                  const hideDivider =
                    f.name === BUS_ATTENDANCE || f.name === CAMPUS_ATTENDANCE;
                  return (
                    <div
                      key={f.name}
                      className={`flex items-center gap-3.5 py-2  last:border-b-0 ${
                        hideDivider ? "" : "mt-2 border-b"
                      } ${
                        isLockedAddOn
                          ? ""
                          : isAttendanceInconsistency
                            ? ""
                            : "cursor-pointer"
                      }`}
                      onClick={() =>
                        !isLockedAddOn &&
                        !isAttendanceInconsistency &&
                        onToggleFeature(f.name)
                      }
                    >
                      <div
                        className={`flex w-5 flex-shrink-0 justify-center text-default-800 ${
                          isLockedAddOn ? "opacity-50" : ""
                        }`}
                      >
                        {f.icon}
                      </div>
                      <div className="flex-1">
                        <b
                          className={`mb-0.5 block text-sm font-bold ${
                            isLockedAddOn ? "opacity-50" : ""
                          }`}
                        >
                          {f.name}
                        </b>
                        <span
                          className={`block text-xs leading-relaxed text-default-400 ${
                            isLockedAddOn ? "opacity-50" : ""
                          }`}
                        >
                          {f.desc}
                        </span>
                        {isLockedAddOn &&
                          attendanceInconsistencyLockedMessage && (
                            <span
                              className="mb-2 mt-1 flex items-start gap-1 text-[12px] font-semibold leading-tight"
                              style={{color: "#FF8F00"}}
                            >
                              <RiInformationLine className="h-4 w-4" />
                              {attendanceInconsistencyLockedMessage}
                            </span>
                          )}
                      </div>
                      {isAttendanceInconsistency ? (
                        isLockedAddOn ? (
                          <span
                            className="flex flex-shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold"
                            style={{
                              background: `${AMBER}1A`,
                              borderColor: `${AMBER}59`,
                              color: "#FF8F00"
                            }}
                          >
                            <BsLightningChargeFill className="h-3 w-3" />
                            Explore
                          </span>
                        ) : (
                          <span
                            className="flex flex-shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold"
                            style={{
                              background: `${AMBER}1A`,
                              borderColor: `${AMBER}59`,
                              color: "#FF8F00"
                            }}
                          >
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={3}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                            Included
                          </span>
                        )
                      ) : (
                        <div
                          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border-2 transition-all"
                          style={{
                            borderColor: checked ? AMBER : "#DADCE0",
                            background: checked ? AMBER : "transparent"
                          }}
                        >
                          {checked && (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#fff"
                              strokeWidth={3}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <button
            className="mt-4 w-full rounded-full py-4 text-sm font-extrabold text-black/90 disabled:cursor-not-allowed disabled:opacity-45"
            style={{background: AMBER, color: "#1A1300"}}
            disabled={!anySelected}
            onClick={onSubmit}
          >
            Submit request
          </button>
        </>
      ) : (
        <div className="flex flex-col items-center px-4 py-8 text-center">
          <div
            className="mb-5 flex h-[74px] w-[74px] items-center justify-center rounded-full"
            style={{background: AMBER, boxShadow: `0 0 0 8px ${"#F1F2F4"}`}}
          >
            <svg
              width="30"
              height="30"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth={3}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <h2 className="mb-2.5 text-lg font-extrabold">Request submitted</h2>
          <p className="mb-6 max-w-[270px] text-xs leading-relaxed text-default-500">
            Our customer support has been notified, they will reach out to you.
          </p>
          <button
            className="w-full rounded-full bg-black py-4 text-sm font-extrabold text-white hover:bg-black/90"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
};

export default MissingFeaturesWidget;
